import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';

// Generate a unique session ID
export const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Save class completion data to Firestore
export const saveClassData = async (classData) => {
  try {
    console.log('🔥 Firestore Debug: Starting saveClassData with:', classData);
    
    // Check if db is properly initialized
    if (!db) {
      console.error('🔥 Firestore Debug: Database not initialized');
      return { success: false, error: 'Database not initialized' };
    }
    
    const sessionId = generateSessionId();
    console.log('🔥 Firestore Debug: Generated sessionId:', sessionId);
    
    // Prepare the data structure for analytics
    const analyticsData = {
      sessionId,
      timestamp: serverTimestamp(),
      classVersion: classData.classVersion,
      
      // User email (if provided)
      userEmail: classData.userEmail || null,
      
      // Try Each Wine Ratings
      wineRatings: classData.tryEachWineRatings || {},
      
      // Wine Preferences Quiz Results
      winePreferences: classData.winePreferences || {},
      
      // Individual Wine Characteristic Ratings
      wineCharacteristicRatings: classData.wineCharacteristicRatings || {},
      
      // Final Blend Data
      finalBlend: classData.finalBlend || {},
      
      // Wine Varietals Selected
      wineVarietals: classData.wineVarietals || {},
      
      // Analytics Metadata
      sessionDuration: classData.sessionDuration || 0,
      modulesCompleted: classData.modulesCompleted || [],
      emailSent: classData.emailSent || false,
      
      // Additional metadata
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    console.log('🔥 Firestore Debug: Prepared analytics data:', analyticsData);
    console.log('🔥 Firestore Debug: Attempting to add document to collection "classCompletions"');

    // Add document to Firestore
    const docRef = await addDoc(collection(db, 'classCompletions'), analyticsData);
    
    console.log('🔥 Firestore Debug: ✅ Class data saved successfully with ID:', docRef.id);
    console.log('🔥 Firestore Debug: Document path:', docRef.path);
    return { success: true, sessionId, docId: docRef.id };
    
  } catch (error) {
    console.error('🔥 Firestore Debug: ❌ Error saving class data:', error);
    console.error('🔥 Firestore Debug: Error code:', error.code);
    console.error('🔥 Firestore Debug: Error message:', error.message);
    console.error('🔥 Firestore Debug: Full error object:', error);
    return { success: false, error: error.message };
  }
};

// Calculate session duration
export const calculateSessionDuration = (startTime) => {
  return Math.floor((Date.now() - startTime) / 1000); // seconds
};

// Get modules completed based on available data
export const getModulesCompleted = (classData) => {
  const modules = [];
  
  if (classData.tryEachWineRatings && Object.keys(classData.tryEachWineRatings).length > 0) {
    modules.push('tryEachWine');
  }
  
  if (classData.winePreferences && Object.keys(classData.winePreferences).length > 0) {
    modules.push('preferences');
  }
  
  if (classData.wineCharacteristicRatings && Object.keys(classData.wineCharacteristicRatings).length > 0) {
    modules.push('rateEachWine');
  }
  
  if (classData.finalBlend && classData.finalBlend.blendName) {
    modules.push('finalBlend');
  }
  
  if (classData.wineVarietals && Object.keys(classData.wineVarietals).length > 0) {
    modules.push('varietals');
  }
  
  return modules;
};

// Update existing class completion record with email
export const updateClassDataWithEmail = async (docId, userEmail) => {
  try {
    console.log('🔥 Firestore Debug: Updating document with email:', { docId, userEmail });
    
    if (!db) {
      console.error('🔥 Firestore Debug: Database not initialized');
      return { success: false, error: 'Database not initialized' };
    }
    
    if (!docId) {
      console.error('🔥 Firestore Debug: Document ID is required for update');
      return { success: false, error: 'Document ID is required' };
    }
    
    const docRef = doc(db, 'classCompletions', docId);
    
    await updateDoc(docRef, {
      userEmail: userEmail,
      emailSent: true,
      emailTimestamp: serverTimestamp()
    });
    
    console.log('🔥 Firestore Debug: ✅ Document updated with email successfully');
    return { success: true };
    
  } catch (error) {
    console.error('🔥 Firestore Debug: ❌ Error updating document with email:', error);
    return { success: false, error: error.message };
  }
};
